import i18n from 'src/i18n';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ENABLE_RAG_SETUP_OPTION } from 'src/constants/featureFlags';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import ChatLoader from 'src/components/loading/chat-loader.component';
import SetupHeaderCard from 'src/components/setup/setup-header-card.component';
import StatusSwitch from 'src/components/switch/status-switch.component';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';

const defaultSettings = {
  ai_provider: 'openai',
  answer_mode: 'openai_thread',
  is_enabled: false,
  has_openai_key: false,
  has_gemini_key: false,
  has_rag: false,
};

const supportedDocumentExtensions = ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'md', 'csv', 'json', 'html', 'htm', 'xml'];
const supportedDocumentText = 'PDF, DOCX, XLSX, PPTX, TXT, MD, CSV, JSON, HTML, or XML';
const supportedDocumentAccept = supportedDocumentExtensions.map((ext) => `.${ext}`).join(',');
const sectionPaperSx = {
  p: 2.5,
  borderRadius: 2,
  width: '100%',
};

const getFileExtension = (fileName = '') => fileName.split('.').pop()?.toLowerCase() || '';
const formatAIAnswerPreview = (text = '') => text.replace(/\s+\*\s+/g, '\n• ').replace(/\*\*/g, '').trim();


export default function AIChatSetup() {
  const { t: translate } = useTranslation();
  const [settings, setSettings] = useState(defaultSettings);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingEnabled, setSavingEnabled] = useState(false);
  const [indexingDocument, setIndexingDocument] = useState(false);
  const [sourceLoading, setSourceLoading] = useState({});
  const [documentForm, setDocumentForm] = useState({ source_name: '', media_url: '', content: '' });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [testQuestion, setTestQuestion] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const fileInputRef = useRef(null);
  const { showToast } = useToaster();
  const hasAIChatPlan = CommonHelper.HasAIChatPlan();
  const canManageAIChat = hasAIChatPlan && CommonHelper.CheckForPermissionAvailableForUser(CommonHelper.PermissionList.AI_CHAT_MANAGE);
  const hasOpenAIKey = Boolean(settings.has_openai_key);
  const hasGeminiKey = Boolean(settings.has_gemini_key);
  const hasRAG = Boolean(settings.has_rag);
  const answerMode = settings.answer_mode === 'rag' && hasRAG ? 'rag' : 'openai_thread';
  const hasAnyProviderKey = Boolean(hasOpenAIKey || hasGeminiKey);
  const hasBothProviderKeys = Boolean(hasOpenAIKey && hasGeminiKey);
  const activeProvider = answerMode === 'openai_thread' ? 'openai' : hasGeminiKey && !hasOpenAIKey ? 'gemini' : settings.ai_provider || 'openai';
  const activeHasKey = activeProvider === 'gemini' ? settings.has_gemini_key : settings.has_openai_key;
  const setupSaved = Boolean(settings.id || sources.length > 0);
  const canShowDocumentSetup = Boolean(activeHasKey && setupSaved);
  const setupDisabled = !settings.is_enabled;
  const hasOpenAIThreadDocument = answerMode === 'openai_thread' && sources.some((source) => source.answer_mode === 'openai_thread');
  const uploadDisabled = Boolean(setupDisabled || indexingDocument || hasOpenAIThreadDocument);
  const disabledContentSx = setupDisabled
    ? {
        opacity: 0.45,
        filter: 'grayscale(0.35)',
        pointerEvents: 'none',
        transition: 'opacity 160ms ease, filter 160ms ease',
      }
    : {
        opacity: 1,
        transition: 'opacity 160ms ease, filter 160ms ease',
      };

  const fetchSettings = useCallback(() => {
    if (!canManageAIChat) {
      setLoading(false);
      return;
    }
    setLoading(true);
    HttpServices.Get({
      url: userModuleURL.aiChatSettings,
      successCallback: (data) => {
        const nextSettings = { ...defaultSettings, ...(data || {}) };
        setSettings(nextSettings);
        setLoading(false);
      },
      failureCallback: (message) => {
        setLoading(false);
        message && showToast(message, 'e');
      },
    });
  }, [canManageAIChat, showToast]);

  const fetchSources = useCallback(() => {
    if (!canManageAIChat) return;
    setSourcesLoading(true);
    HttpServices.Get({
      url: userModuleURL.aiChatSources,
      successCallback: (data) => {
        setSources(Array.isArray(data) ? data : []);
        setSourcesLoading(false);
      },
      failureCallback: (message) => {
        setSourcesLoading(false);
        message && showToast(message, 'e');
      },
    });
  }, [canManageAIChat, showToast]);

  useEffect(() => {
    fetchSettings();
    fetchSources();
  }, [fetchSettings, fetchSources]);

  const saveSettings = () => {
    const mode = answerMode;
    const provider = mode === 'openai_thread' ? 'openai' : hasGeminiKey && !hasOpenAIKey ? 'gemini' : settings.ai_provider || 'openai';
    const hasActiveKey = provider === 'gemini' ? settings.has_gemini_key : settings.has_openai_key;
    if (!hasActiveKey) {
      showToast(translate('ai_chat_setup_page.add_api_key_before_saving', { provider: provider === 'gemini' ? 'Gemini' : 'OpenAI', defaultValue: 'Add the {{provider}} API key before saving AI chat settings.' }), 'e');
      return;
    }
    setSavingSettings(true);
    HttpServices.Post({
      url: userModuleURL.aiChatSettings,
      body: {
        ai_provider: provider,
        answer_mode: mode,
        is_enabled: Boolean(settings.is_enabled),
      },
      successCallback: (data, message) => {
        setSettings({ ...defaultSettings, ...(data || {}) });
        setSavingSettings(false);
        message && showToast(message, 's');
      },
      failureCallback: (message) => {
        setSavingSettings(false);
        message && showToast(message, 'e');
      },
    });
  };

  const uploadDocument = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    if (setupDisabled) {
      event.target.value = '';
      return;
    }
    if (!canShowDocumentSetup) {
      showToast(translate('ai_chat_setup_page.save_setup_before_upload', { defaultValue: 'Save the AI chat setup before uploading a document.' }), 'e');
      event.target.value = '';
      return;
    }
    if (hasOpenAIThreadDocument) {
      showToast(
        translate('ai_chat_setup_page.delete_thread_document_before_upload', {
          defaultValue: 'OpenAI thread already has a document. Delete the existing document before uploading another.',
        }),
        'e'
      );
      event.target.value = '';
      return;
    }
    const unsupportedFile = files.find((file) => !supportedDocumentExtensions.includes(getFileExtension(file.name)));
    if (unsupportedFile) {
      showToast(
        translate('ai_chat_setup_page.unsupported_document_type', {
          extension: getFileExtension(unsupportedFile.name),
          formats: supportedDocumentText,
          defaultValue: 'Unsupported document type .{{extension}}. Upload {{formats}}',
        }),
        'e'
      );
      event.target.value = '';
      return;
    }
    const formData = new FormData();
    files.forEach((file) => formData.append('fileupload', file));
    HttpServices.UploadMediaFiles({
      formData,
      setFileUploadprogress: setUploadProgress,
      successCallback: async (uploadedFiles) => {
        const documents = (uploadedFiles || [])
          .map((uploaded, index) => ({
            source_name: files[index]?.name || `Uploaded document ${index + 1}`,
            media_url: uploaded.media_url || '',
            content: '',
          }))
          .filter((document) => document.media_url);
        if (!documents.length) {
          showToast(
            translate('ai_chat_setup_page.no_uploaded_document_url', {
              defaultValue: 'No uploaded document URL found.',
            }),
            'e'
          );
          return;
        }
        setDocumentForm(documents[documents.length - 1]);
        await saveSources(documents);
      },
      failureCallback: (message) => message && showToast(message, 'e'),
    });
    event.target.value = '';
  };

  const indexDocumentSource = (sourceForm, provider) =>
    new Promise((resolve, reject) => {
      HttpServices.Post({
        url: userModuleURL.aiChatDocumentSource,
        body: { ...sourceForm, ai_provider: provider, answer_mode: answerMode },
        successCallback: (data, message) => resolve({ data, message }),
        failureCallback: (message) =>
          reject(
            new Error(
              message ||
                translate('ai_chat_setup_page.failed_to_index_document', {
                  defaultValue: 'Failed to index document',
                })
            )
          ),
      });
    });

  const indexOpenAIThreadDocuments = (documents, provider) =>
    new Promise((resolve, reject) => {
      HttpServices.Post({
        url: userModuleURL.aiChatDocumentSource,
        body: { ai_provider: provider, answer_mode: answerMode, documents },
        successCallback: (data, message) => resolve({ data, message }),
        failureCallback: (message) =>
          reject(
            new Error(
              message ||
                translate('ai_chat_setup_page.failed_to_index_document', {
                  defaultValue: 'Failed to index document',
                })
            )
          ),
      });
    });

  const saveSources = async (sourceForms = [documentForm]) => {
    const provider = answerMode === 'openai_thread' ? 'openai' : hasGeminiKey && !hasOpenAIKey ? 'gemini' : settings.ai_provider || 'openai';
    const hasActiveKey = provider === 'gemini' ? settings.has_gemini_key : settings.has_openai_key;
    if (!hasActiveKey) {
      showToast(
        translate('ai_chat_setup_page.add_api_key_before_indexing', {
          provider: provider === 'gemini' ? 'Gemini' : 'OpenAI',
          defaultValue: 'Add the {{provider}} API key before indexing the document.',
        }),
        'e'
      );
      return;
    }
    const documents = sourceForms.filter((sourceForm) => sourceForm.media_url);
    if (!documents.length) {
      showToast(
        translate('ai_chat_setup_page.upload_before_indexing', {
          defaultValue: 'Upload a document before indexing.',
        }),
        'e'
      );
      return;
    }
    setIndexingDocument(true);
    let indexedCount = 0;
    if (answerMode === 'openai_thread') {
      try {
        await indexOpenAIThreadDocuments(documents, provider);
        indexedCount = documents.length;
      } catch (error) {
        showToast(error.message, 'e');
      }
    } else {
      for (const document of documents) {
        try {
          await indexDocumentSource(document, provider);
          indexedCount += 1;
        } catch (error) {
          showToast(`${document.source_name}: ${error.message}`, 'e');
        }
      }
    }
    setSettings((prev) => ({
      ...prev,
      ai_provider: provider,
      answer_mode: answerMode,
    }));
    setDocumentForm({ source_name: '', media_url: '', content: '' });
    setIndexingDocument(false);
    fetchSources();
    if (indexedCount > 0) {
      showToast(
        translate('ai_chat_setup_page.documents_indexed_successfully', {
          count: indexedCount,
          defaultValue: '{{count}} document indexed successfully',
        }),
        's'
      );
    }
  };

  const deleteSource = (sourceId) => {
    if (setupDisabled) {
      return;
    }
    setSourceLoading((prev) => ({ ...prev, [sourceId]: true }));
    HttpServices.Post({
      url: `${userModuleURL.aiChatSourceDelete}/${sourceId}`,
      body: {},
      successCallback: (_data, message) => {
        setSourceLoading((prev) => ({ ...prev, [sourceId]: false }));
        setDocumentForm({ source_name: '', media_url: '', content: '' });
        fetchSources();
        message && showToast(message, 's');
      },
      failureCallback: (message) => {
        setSourceLoading((prev) => ({ ...prev, [sourceId]: false }));
        message && showToast(message, 'e');
      },
    });
  };

  const testAssistant = () => {
    if (setupDisabled) {
      return;
    }
    if (!testQuestion.trim()) {
      showToast(
        translate('ai_chat_setup_page.enter_question_to_test', {
          defaultValue: 'Enter a question to test',
        }),
        'e'
      );
      return;
    }
    setTesting(true);
    HttpServices.Post({
      url: userModuleURL.aiChatTestAnswer,
      body: { question: testQuestion },
      successCallback: (data) => {
        setTestResult(data?.answer || '');
        setTesting(false);
      },
      failureCallback: (message) => {
        setTesting(false);
        message && showToast(message, 'e');
      },
    });
  };

  const toggleAISetup = (event) => {
    const previousEnabled = Boolean(settings.is_enabled);
    const nextEnabled = event.target.checked;
    const provider = answerMode === 'openai_thread' ? 'openai' : hasGeminiKey && !hasOpenAIKey ? 'gemini' : settings.ai_provider || 'openai';

    setSettings((prev) => ({ ...prev, is_enabled: nextEnabled }));
    setSavingEnabled(true);
    HttpServices.Post({
      url: userModuleURL.aiChatSettings,
      body: {
        ai_provider: provider,
        answer_mode: answerMode,
        is_enabled: nextEnabled,
      },
      successCallback: (data, message) => {
        setSettings({ ...defaultSettings, ...(data || {}) });
        setSavingEnabled(false);
        message && showToast(message, 's');
      },
      failureCallback: (message) => {
        setSettings((prev) => ({ ...prev, is_enabled: previousEnabled }));
        setSavingEnabled(false);
        message && showToast(message, 'e');
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>{translate('ai_chat_setup_page.title', { defaultValue: 'AI Chat Setup' })}</title>
      </Helmet>
      <Box sx={{ height: 'calc(100vh - 70px)', minHeight: 0, overflow: 'hidden', pb: 2 }}>
        <Stack
          spacing={2.5}
          sx={{
            height: '100%',
            minHeight: 0,
            overflowY: 'auto',
            px: 0.75,
            mx: 'auto',
            pb: { xs: 10, md: 6 },
            scrollbarGutter: 'stable both-edges',
          }}
        >
          <SetupHeaderCard
            icon={<SmartToyRoundedIcon />}
            title={translate('ai_chat_setup_page.title', { defaultValue: 'AI Chat Setup' })}
            subtitle={translate('ai_chat_setup_page.subtitle', {
              defaultValue: 'Add an API key first, then upload a document and test visitor answers.',
            })}
            action={
              canManageAIChat ? (
                <StatusSwitch
                  checked={Boolean(settings.is_enabled)}
                  disabled={loading || savingEnabled}
                  onChange={toggleAISetup}
                  inputProps={{
                    'aria-label': settings.is_enabled
                      ? translate('ai_chat_setup_page.disable_ai_setup', { defaultValue: 'Disable AI chat setup' })
                      : translate('ai_chat_setup_page.enable_ai_setup', { defaultValue: 'Enable AI chat setup' }),
                  }}
                />
              ) : null
            }
          />

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, flexShrink: 0, ...disabledContentSx }}>
            <Stack spacing={2.5}>
              {loading ? (
                <ChatLoader
                  label={translate('ai_chat_setup_page.loading', { defaultValue: 'Loading AI chat setup' })}
                  description={translate('ai_chat_setup_page.loading_description', {
                    defaultValue: 'Preparing your AI chat experience',
                  })}
                />
              ) : !hasAIChatPlan ? (
                <Alert severity="info" action={<Button component={RouterLink} to="/billing" size="small">{i18n.t('plan_access.view_plans')}</Button>}>
                  {i18n.t('plan_access.ai_upgrade')}
                </Alert>
              ) : !canManageAIChat ? (
                <Typography variant="body2" color="text.secondary">{translate('ai_chat_setup_page.permission_note', { defaultValue: 'AI chat setup requires AI chat manage permission.' })}</Typography>
              ) : (
                <Stack spacing={2}>
                  <Alert
                    severity={hasAnyProviderKey ? 'info' : 'warning'}
                    action={
                      <Button component={RouterLink} to="/manage-brand" size="small" variant="contained">
                        {translate('ai_chat_setup_page.open_brand_settings', { defaultValue: 'Open Brand Settings' })}
                      </Button>
                    }
                  >
                    <Typography variant="subtitle2">
                      {translate('ai_chat_setup_page.api_key_location_title', { defaultValue: 'API keys are managed in Brand Settings' })}
                    </Typography>
                    <Typography variant="body2">
                      {translate('ai_chat_setup_page.api_key_location_body', {
                        defaultValue:
                          'Add or update OpenAI and Gemini keys from Brand Settings. Come back here to choose the provider and upload documents.',
                      })}
                    </Typography>
                  </Alert>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                          <Box>
                            <Typography variant="subtitle2">OpenAI</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {hasOpenAIKey
                                ? translate('ai_chat_setup_page.openai_key_ready', { defaultValue: 'OpenAI key is added.' })
                                : translate('ai_chat_setup_page.openai_key_missing', { defaultValue: 'OpenAI key is not added yet.' })}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            color={hasOpenAIKey ? 'success' : 'warning'}
                            label={
                              hasOpenAIKey
                                ? translate('ai_chat_setup_page.key_added', { defaultValue: 'Added' })
                                : translate('ai_chat_setup_page.key_missing', { defaultValue: 'Missing' })
                            }
                          />
                        </Stack>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                          <Box>
                            <Typography variant="subtitle2">Gemini</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {hasGeminiKey
                                ? translate('ai_chat_setup_page.gemini_key_ready', { defaultValue: 'Gemini key is added.' })
                                : translate('ai_chat_setup_page.gemini_key_missing', { defaultValue: 'Gemini key is not added yet.' })}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            color={hasGeminiKey ? 'success' : 'warning'}
                            label={
                              hasGeminiKey
                                ? translate('ai_chat_setup_page.key_added', { defaultValue: 'Added' })
                                : translate('ai_chat_setup_page.key_missing', { defaultValue: 'Missing' })
                            }
                          />
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>
                  {!hasAnyProviderKey ? (
                    <Typography variant="body2" color="text.secondary">
                      {translate('ai_chat_setup_page.add_key_to_continue', {
                        defaultValue: 'Add at least one API key in Brand Settings to continue AI chat setup.',
                      })}
                    </Typography>
                  ) : (
                    <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      fullWidth
                      label={translate('ai_chat_setup_page.answer_mode', { defaultValue: 'Answer mode' })}
                      disabled={setupDisabled || sources.length > 0}
                      value={answerMode}
                      onChange={(e) => {
                        const nextMode = e.target.value;
                        if (nextMode === 'rag' && (!ENABLE_RAG_SETUP_OPTION || !hasRAG)) return;
                        setSettings((prev) => ({
                          ...prev,
                          answer_mode: nextMode,
                          ai_provider: nextMode === 'openai_thread' ? 'openai' : prev.ai_provider,
                        }));
                      }}
                      helperText={
                        !ENABLE_RAG_SETUP_OPTION
                          ? i18n.t('plan_access.rag_unavailable')
                          : sources.length > 0
                          ? translate('ai_chat_setup_page.delete_document_before_mode_change', {
                              defaultValue: 'Delete the indexed document before changing answer mode.',
                            })
                          : !hasRAG
                            ? translate('ai_chat_setup_page.rag_requires_payment', {
                                defaultValue: 'Upgrade your plan to enable RAG and reduce AI response cost.',
                              })
                            : translate('ai_chat_setup_page.answer_mode_help', {
                                defaultValue: 'Choose direct OpenAI thread upload or RAG document search.',
                              })
                      }
                    >
                      <MenuItem value="openai_thread">
                        {translate('ai_chat_setup_page.openai_thread_mode', { defaultValue: 'OpenAI thread upload' })}
                      </MenuItem>
                      <MenuItem value="rag" disabled={!ENABLE_RAG_SETUP_OPTION || !hasRAG}>
                        {translate('ai_chat_setup_page.rag_mode', { defaultValue: 'RAG document search' })}
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {hasBothProviderKeys ? (
                      <TextField
                        select
                        fullWidth
                        label={translate('ai_chat_setup_page.ai_provider', { defaultValue: 'AI Provider' })}
                        disabled={setupDisabled || sources.length > 0 || answerMode === 'openai_thread'}
                        value={activeProvider}
                        onChange={(e) => setSettings((prev) => ({ ...prev, ai_provider: e.target.value }))}
                        helperText={
                          answerMode === 'openai_thread'
                            ? translate('ai_chat_setup_page.openai_thread_requires_openai', {
                                defaultValue: 'OpenAI thread upload uses OpenAI only.',
                              })
                            : sources.length > 0
                            ? translate('ai_chat_setup_page.delete_document_before_provider_change', {
                                defaultValue: 'Delete the indexed document before changing provider.',
                              })
                            : translate('ai_chat_setup_page.both_api_keys_available', {
                                defaultValue: 'Both API keys are available.',
                              })
                        }
                      >
                        <MenuItem value="openai">OpenAI</MenuItem>
                        <MenuItem value="gemini">Gemini</MenuItem>
                      </TextField>
                    ) : (
                      <TextField
                        fullWidth
                        disabled={setupDisabled}
                        label={translate('ai_chat_setup_page.ai_provider', { defaultValue: 'AI Provider' })}
                        value={activeProvider === 'gemini' ? 'Gemini' : 'OpenAI'}
                        helperText={translate('ai_chat_setup_page.api_key_configured', {
                          provider: activeProvider === 'gemini' ? 'Gemini' : 'OpenAI',
                          defaultValue: '{{provider}} API key is configured.',
                        })}
                        InputProps={{ readOnly: true }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color={activeHasKey ? 'primary.main' : 'error.main'}>
                      {activeHasKey
                        ? translate('ai_chat_setup_page.api_key_configured_short', {
                            provider: activeProvider === 'gemini' ? 'Gemini' : 'OpenAI',
                            defaultValue: '{{provider}} API key configured',
                          })
                        : translate('ai_chat_setup_page.api_key_required', {
                            provider: activeProvider === 'gemini' ? 'Gemini' : 'OpenAI',
                            defaultValue: '{{provider}} API key required',
                          })}
                    </Typography>
                    {!setupSaved && (
                      <Typography variant="body2" color="text.secondary">
                        {translate('ai_chat_setup_page.save_selected_setup_before_upload', {
                          defaultValue: 'Save the selected setup before uploading a document.',
                        })}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <LoadingButton fullWidth variant="contained" loading={savingSettings} disabled={setupDisabled} onClick={saveSettings}>{translate('ai_chat_setup_page.save_settings', { defaultValue: 'Save Settings' })}</LoadingButton>
                  </Grid>
                    </Grid>
                  )}
                </Stack>
              )}
            </Stack>
          </Paper>

          {!loading && canManageAIChat && canShowDocumentSetup && (
            <>
              <Stack spacing={2.5}>
                <Paper variant="outlined" sx={{ ...sectionPaperSx, ...disabledContentSx }}>
                  <Stack spacing={1.75}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <DescriptionIcon color="primary" />
                      <Typography variant="h6">{translate('ai_chat_setup_page.document_source', { defaultValue: 'Document Source' })}</Typography>
                    </Stack>
                    <input ref={fileInputRef} type="file" accept={supportedDocumentAccept} multiple hidden onChange={uploadDocument} />
                    <Button size="small" variant="outlined" startIcon={<UploadFileIcon />} disabled={uploadDisabled} onClick={() => fileInputRef.current?.click()}>
                      {translate('ai_chat_setup_page.upload_document', { defaultValue: 'Upload Documents' })}
                    </Button>
                    {hasOpenAIThreadDocument && (
                      <Alert severity="info">
                        {translate('ai_chat_setup_page.delete_thread_document_before_upload', {
                          defaultValue: 'OpenAI thread already has a document. Delete the existing document before uploading another.',
                        })}
                      </Alert>
                    )}
                    {(indexingDocument || (uploadProgress > 0 && uploadProgress < 100)) && (
                      <LinearProgress variant={uploadProgress > 0 && uploadProgress < 100 ? 'determinate' : 'indeterminate'} value={uploadProgress} />
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {translate('ai_chat_setup_page.supported_documents', { defaultValue: `Supported formats: ${supportedDocumentText}` })}
                    </Typography>
                    {documentForm.media_url ? (
                      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
                          <Typography variant="body2">{documentForm.source_name || translate('ai_chat_setup_page.uploaded_document', { defaultValue: 'Uploaded document' })}</Typography>
                          <Button size="small" variant="text" disabled={setupDisabled} onClick={() => setDocumentForm({ source_name: '', media_url: '', content: '' })}>{translate('ai_chat_setup_page.remove', { defaultValue: 'Remove' })}</Button>
                        </Stack>
                      </Paper>
                    ) : null}
                  </Stack>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    ...sectionPaperSx,
                    ...disabledContentSx,
                    bgcolor: 'action.hover',
                    borderStyle: 'dashed',
                    opacity: 0.82,
                  }}
                >
                  <Stack spacing={1.75}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LinkIcon color="disabled" />
                        <Typography variant="h6" color="text.secondary">
                          {translate('ai_chat_setup_page.website_link_source', { defaultValue: 'Website Link' })}
                        </Typography>
                      </Stack>
                      <Chip size="small" label={translate('ai_chat_setup_page.coming_soon', { defaultValue: 'Coming soon' })} />
                    </Stack>
                    <TextField
                      fullWidth
                      size="small"
                      disabled
                      label={translate('ai_chat_setup_page.website_url', { defaultValue: 'Website URL' })}
                      placeholder="https://example.com"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {translate('ai_chat_setup_page.website_link_coming_soon_help', {
                        defaultValue: 'Website crawling will be available soon. For now, upload documents to train AI chat.',
                      })}
                    </Typography>
                  </Stack>
                </Paper>
              </Stack>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, ...disabledContentSx }}>
                <Stack spacing={2}>
                  <Typography variant="h6">{translate('ai_chat_setup_page.knowledge_sources', { defaultValue: 'Knowledge Sources' })}</Typography>
                  {sourcesLoading ? (
                    <ChatLoader
                      minHeight={220}
                      label={translate('ai_chat_setup_page.loading_sources', {
                        defaultValue: 'Loading knowledge sources',
                      })}
                      description={translate('ai_chat_setup_page.loading_sources_description', {
                        defaultValue: 'Gathering your AI knowledge',
                      })}
                    />
                  ) : (
                    <Stack spacing={1} sx={{ maxHeight: 320, overflowY: 'auto', pr: 1 }}>
                      {sources.map((source) => (
                        <Paper key={source.id} variant="outlined" sx={{ p: 1.5 }}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle2">{source.source_name}</Typography>
                            </Box>
                            <IconButton color="error" disabled={setupDisabled || Boolean(sourceLoading[source.id])} onClick={() => deleteSource(source.id)}><DeleteOutlineIcon /></IconButton>
                          </Stack>
                        </Paper>
                      ))}
                      {!sources.length && <Typography variant="body2" color="text.secondary">{translate('ai_chat_setup_page.no_sources', { defaultValue: 'No AI knowledge sources yet.' })}</Typography>}
                    </Stack>
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, ...disabledContentSx }}>
                <Stack spacing={2}>
                  <Typography variant="h6">{translate('ai_chat_setup_page.test_visitor_answer', { defaultValue: 'Test Visitor Answer' })}</Typography>
                  <TextField fullWidth multiline minRows={3} disabled={setupDisabled} label={translate('ai_chat_setup_page.ask_test_question', { defaultValue: 'Ask a test question' })} value={testQuestion} onChange={(e) => setTestQuestion(e.target.value)} />
                  <LoadingButton variant="contained" loading={testing} disabled={setupDisabled} onClick={testAssistant}>{translate('ai_chat_setup_page.ask_ai', { defaultValue: 'Ask AI' })}</LoadingButton>
                  {testResult && (
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>{translate('ai_chat_setup_page.answer', { defaultValue: 'Answer' })}</Typography>
                      <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: 1.7, m: 0 }}>
                        {formatAIAnswerPreview(testResult)}
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </Paper>
            </>
          )}
        </Stack>
      </Box>
    </>
  );
}
